import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ToolManager } from '~/engine/tool/ToolManager';
import {
    LineOptions,
    lineOptionsState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';
import { GridLayoutMode, gridLayoutModeState } from '~/state/atoms/viewerAtoms';

const TOGGLE_KEY = 'e';

/**
 * Pure logic: return the line mode after one erase toggle. A `null` mode is
 * treated as not-erasing (matching the toolbar toggle, whose off state writes
 * `'draw'`), so it flips to `'erase'`.
 */
export function nextLineMode(mode: LineOptions['mode']): 'draw' | 'erase' {
    return mode === 'erase' ? 'draw' : 'erase';
}

/**
 * Hook: listen for the plain E key and flip the Line tool between draw and
 * erase mode. Writes the same `lineOptionsState` atom as the toolbar toggle.
 *
 * Guards (early-return so other handlers still process the event):
 *  - Any modifier (Ctrl/Cmd/Alt/Shift) held — preserves OS/browser shortcuts.
 *  - The event is an auto-repeat (avoids rapid toggles when the key is held).
 *  - The active element is an INPUT, TEXTAREA, or contentEditable element.
 *  - GridLayoutMode is not Default (e.g. SideBySide registration mode).
 *  - A tool action is in progress (e.g. a half-drawn line) — never toggle
 *    mid-stroke.
 *  - The Line tool is not the selected tool.
 */
export function useLineEraseToggleShortcut(): void {
    const selectedTool = useRecoilValue(selectedToolState);
    const gridLayoutMode = useRecoilValue(gridLayoutModeState);
    const setLineOptions = useSetRecoilState(lineOptionsState);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                event.shiftKey
            ) {
                return;
            }
            if (event.repeat) {
                return;
            }
            if (event.key.toLowerCase() !== TOGGLE_KEY) {
                return;
            }
            if (selectedTool !== 'line') {
                return;
            }
            if (gridLayoutMode !== GridLayoutMode.Default) {
                return;
            }
            const activeElement = document.activeElement as HTMLElement | null;
            if (
                activeElement &&
                (activeElement.tagName === 'INPUT' ||
                    activeElement.tagName === 'TEXTAREA' ||
                    activeElement.isContentEditable)
            ) {
                return;
            }
            if (ToolManager.getActiveTool()?.isActionInProgress()) {
                return;
            }
            setLineOptions((options) => ({
                ...options,
                mode: nextLineMode(options.mode),
            }));
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [selectedTool, gridLayoutMode, setLineOptions]);
}
